import { Suspense } from 'react';
import BuilderHeader from '@/components/new-builder/BuilderHeader';
import { Outlet, useParams, useLocation, Navigate } from 'react-router-dom';
import { ROUTES } from '@/routes/routeConfig';
import { LoaderIcon } from 'lucide-react';
import {
  BuilderHeaderProvider,
  useBuilderHeader,
} from '@/contexts/BuilderHeaderContext';

function BuilderLayoutContent() {
  const { landingPageID, interfaceId } = useParams<{
    landingPageID: string;
    interfaceId?: string;
  }>();
  const location = useLocation();
  const { builderState, showPreview, setShowPreview } = useBuilderHeader();

  // If pathname matches exactly /builder/{moduleId}/{interfaceId} (with or without trailing slash)
  if (landingPageID && interfaceId) {
    const basePath = `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}`;
    // Normalize paths by removing trailing slashes for comparison
    const normalizedPath = location.pathname.replace(/\/+$/, '');
    const normalizedBasePath = basePath.replace(/\/+$/, '');

    const hasTemplatePreview = location.pathname.includes(
      ROUTES.BUILDER_TEMPLATE_PREVIEW
    );
    const hasEditor = location.pathname.includes(ROUTES.BUILDER_EDITOR);
    const hasSettings = location.pathname.includes(ROUTES.BUILDER_SETTINGS);
    const hasPublish = location.pathname.includes(ROUTES.BUILDER_PUBLISH);

    // If we're on the base route (exactly /builder/{moduleId}/{interfaceId} with optional trailing slash)
    // and not already on template-preview, editor, settings, or publish, redirect
    const isBaseRoute =
      normalizedPath === normalizedBasePath &&
      !hasTemplatePreview &&
      !hasEditor &&
      !hasSettings &&
      !hasPublish;

    if (isBaseRoute) {
      const redirectPath = `${ROUTES.BUILDER}/${landingPageID}/${interfaceId}/${ROUTES.BUILDER_TEMPLATE_PREVIEW}`;
      console.log('[BuilderLayout] Redirecting to:', redirectPath);
      return <Navigate to={redirectPath} replace />;
    }
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-purple-50 to-indigo-50 overflow-hidden">
      <BuilderHeader
        setShowPreview={setShowPreview}
        builderState={builderState || undefined}
      />
      <div className="flex-1 min-h-0 overflow-hidden">
        <Suspense fallback={<OutletLoader />}>
          <Outlet context={{ showPreview, setShowPreview }} />
        </Suspense>
      </div>
    </div>
  );
}

export default function BuilderLayout() {
  return (
    <BuilderHeaderProvider>
      <BuilderLayoutContent />
    </BuilderHeaderProvider>
  );
}

// Loading component for the outlet content area only
const OutletLoader = () => (
  <div className="h-full w-full flex items-center justify-center">
    <div className="flex flex-col items-center space-y-4">
      <LoaderIcon className="animate-spin h-8 w-8 text-builder-primary" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);
